import { Router } from '@angular/router';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import {
    BehaviorSubject,
    lastValueFrom,
    Observable,
    of,
    switchMap,
    tap,
} from 'rxjs';

import { environment } from '@env/environment';
import { ResetPassword, SignUpModel } from './auth.type';
import { ServerMsgResponse } from '@domain/entities/general.model';
import { AccessToken, LoginModel, UserOnSession } from './auth.type';

@Injectable({ providedIn: 'root' })
export class AuthService {
    private readonly accessTokenName = 'access_token';

    /**
     * Store for user on session
     */
    public $session = new BehaviorSubject<UserOnSession | null>(null);
    public session = this.$session.asObservable();

    constructor(private _router: Router, private _http: HttpClient) {}

    // --------------------------------------------------------------------------
    // @ Getters
    // --------------------------------------------------------------------------

    /**
     * @description Get current access token
     */
    get accessToken(): string {
        return localStorage.getItem(this.accessTokenName) ?? '';
    }

    // --------------------------------------------------------------------------
    // @ Setters
    // --------------------------------------------------------------------------

    /**
     * @description set access token
     */
    set accessToken(token: string) {
        localStorage.setItem(this.accessTokenName, token);
    }

    /**
     * Do the logic of login
     * @param credentials
     */
    public getUserMe(): Observable<UserOnSession> {
        const path = `${environment.host}v1/users/me`;
        return this._http.get<UserOnSession>(path);
    }

    /**
     *
     * @returns
     */
    public async checkAuthentication(): Promise<UserOnSession | null> {
        let userOnSession = this.$session.getValue();
        if (userOnSession) return userOnSession;

        if (this.accessToken) {
            try {
                let session = await lastValueFrom(this.getUserMe());

                this.$session.next(session);

                return session;
            } catch (error) {
                this.signOut();
                return null;
            }
        } else {
            this.signOut();
            return null;
        }
    }

    /**
     * @description setting acces token and refresh token.
     * @description decode jwt token
     * @param response AccessToken
     */
    private saveToken(response: AccessToken) {
        // Store the access token in the local storage
        this.accessToken = response.token;
    }

    // --------------------------------------------------------------------------
    // @ Public methods
    // --------------------------------------------------------------------------

    /**
     * @description sign in on app
     */
    public signIn(credentials: Partial<LoginModel>): Observable<boolean> {
        return this._http
            .post(`${environment.host}v1/auth/login`, credentials)
            .pipe(
                switchMap((response: AccessToken) => {
                    // decode and save token
                    this.saveToken(response);
                    // Return a new observable with the response
                    return of(!!response);
                })
            );
    }

    /**
     * @description Register user
     * @param form {required}
     * @returns Observable of msg type
     */
    public singUp(form: Partial<SignUpModel>): Observable<ServerMsgResponse> {
        return this._http.post<ServerMsgResponse>(
            `${environment.host}api/v2/users`,
            form
        );
    }

    /**
     * @description Confirm user
     * @param form {required}
     * @returns Observable of msg type
     */
    public confirmUser(form): Observable<ServerMsgResponse> {
        return this._http.put<ServerMsgResponse>(
            `${environment.host}api/v1/users/confirm`,
            form
        );
    }

    /**
     * @description Change password of user on session
     * @param password {required}
     * @returns Observable of msg type
     */
    public resetPassword(form: ResetPassword): Observable<ServerMsgResponse> {
        return this._http.put<ServerMsgResponse>(
            `${environment.host}api/v1/users/password_change_token`,
            form
        );
    }

    /**
     * @description Restore password of user on session
     * @param email {required}
     * @returns Observable of msg type
     */
    public forgotPassword(
        form: Partial<{ email: string }>
    ): Observable<ServerMsgResponse> {
        return this._http.post<ServerMsgResponse>(
            `${environment.host}api/v1/users/password_change_request`,
            form
        );
    }

    /**
     * @description remove items to local storage and close session
     */
    public async signOut() {
        // remove access and refresh token
        localStorage.removeItem(this.accessTokenName);
        // remove info of user on session
        this.$session.next(null);
        //localStorage.removeItem(this.refreshTokenName);

        // re-direct to login
        this._router.navigate(['/auth/sign-in']);
    }
}
